import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  AppBarProps,
  Divider,
  DividerProps,
  Typography,
  Stack,
  Button,
} from '@mui/material';
import { CalendarToday as CalendarTodayIcon } from '@mui/icons-material';

import { getAuthState } from '../../../../store/AuthSlice';
import { useAppSelector } from '../../../../hooks';
import { getAppLogoUrl, isMobileScreen } from '../../../../helper';
import { TP_COLORS } from '../../../../constants';
import HeaderUserProfileOptionsViewer from '../../../V2/Portal/components/HeaderUserProfileOptionsViewer';
import NavigationOptionsIconButton from '../../../../components/Headers/NavigationOptionsIconButton';
import CreditViewer from '../../../V2/Portal/components/CreditViewer';
import UpgradeButton from '../../../V2/Portal/components/UpgradeButton';

interface NavigationBarHeaderProps {
  appBarProps?: AppBarProps;
}

const dividerProps: DividerProps = {
  flexItem: true,
  orientation: 'vertical',
  variant: 'middle',
  sx: { margin: '16px', borderWidth: '1px', backgroundColor: '#00000033' },
};

const NavigationBarHeader: React.FC<NavigationBarHeaderProps> = ({
  appBarProps = {},
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // STORE
  const authState = useAppSelector(getAuthState);

  const isMobile = isMobileScreen();
  const authUser = authState.user;
  const classBookingUrl = `${process.env.REACT_APP_TUTOPIYA_PLATFORM_BASE_URL}/#/dashboard/consultee/consultant`;
  const isSignInSignUpVisible =
    !authUser || (authUser && authUser.role.isGuest);

  const isTrialRecordingsInUrl = window.location.href.includes(
    'trial-recording'
  );

  const isUpgradeVisible =
    isSignInSignUpVisible || (authUser && authUser.role.name === 'Student');

  const redirectURL = location.pathname.replace('/learning-portal', '').trim();

  // RENDER
  return (
    <Box flexGrow={1} {...appBarProps}>
      <AppBar
        elevation={0}
        position="fixed"
        color="inherit"
        sx={{
          backgroundColor: TP_COLORS.white,
          boxShadow: '0px 4px 4px 0px #0000000F',
        }}
      >
        <Toolbar>
          <NavigationOptionsIconButton />
          <Box
            component={Link}
            to={`${process.env.PUBLIC_URL}/`}
            style={{ margin: '3px 0 0 0', padding: 0 }}
          >
            <img
              src={getAppLogoUrl('tp-logo-text-green-new.png')}
              alt="tp-logo"
              style={{ width: '130px', height: '33px' }}
            />
          </Box>
          {!isMobile && (
            <>
              <Divider {...dividerProps} />
              <Typography
                sx={{ fontWeight: 500, fontSize: '20px', color: '#003768' }}
              >
                Learning Platform
              </Typography>
            </>
          )}
          <span style={{ flexGrow: 1 }} />
          <Stack
            direction="row"
            alignItems="center"
            sx={{ display: { xs: 'none', md: 'flex' } }}
            gap={1}
          >
            <CreditViewer />
            {isSignInSignUpVisible && (
              <>
                <div>
                  <Button
                    disableElevation
                    variant="contained"
                    color="inherit"
                    style={{ color: TP_COLORS.black }}
                    onClick={() =>
                      navigate(
                        `${process.env.PUBLIC_URL}/login?${
                          redirectURL ? `redirect=${redirectURL}` : ''
                        }`
                      )
                    }
                  >
                    Sign in
                  </Button>
                </div>
                {!isTrialRecordingsInUrl && (
                  <div>
                    <Button
                      disableElevation
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        navigate(
                          `${process.env.PUBLIC_URL}/sign-up?${
                            redirectURL ? `redirect=${redirectURL}` : ''
                          }`
                        )
                      }
                    >
                      Sign Up
                    </Button>
                  </div>
                )}
                {isTrialRecordingsInUrl && (
                  <div>
                    <Button
                      disableElevation
                      variant="contained"
                      color="primary"
                      href={classBookingUrl}
                      target="_blank"
                      startIcon={<CalendarTodayIcon />}
                    >
                      Book Classes With Tutopiya
                    </Button>
                  </div>
                )}
              </>
            )}
            {isUpgradeVisible && <UpgradeButton />}
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            {authUser && !authUser.role.isGuest ? (
              <HeaderUserProfileOptionsViewer />
            ) : null}
          </Stack>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default NavigationBarHeader;
